/* ==========================================================================
   Rouwkaart Online — beweging

   Drie dingen, alle drie afgekeken van shopify.com:

   1. De hero staat vast terwijl de eerste kleurbaan eroverheen schuift.
      Dat is puur CSS (position: sticky); hier wordt alleen de kop
      bijgehouden die om de paar seconden wisselt.
   2. In de statement-sectie licht telkens één zin op, met de bijbehorende
      beelden: op tijd, of de zin waar je met de muis overheen gaat.
   3. Blokken komen rustig in beeld.

   Alles respecteert `prefers-reduced-motion`.
   ========================================================================== */
(function () {
  'use strict';

  var rustig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- 1 */
  function heroRotator(root) {
    // In de HTML staat alleen de eerste wisselregel, zodat de H1 één leesbare
    // zin is. De rest komt uit `data-words` en krijgt aria-hidden: een
    // schermlezer hoort de kop dus ook als één zin.
    if (!root.dataset.rotatorReady) {
      root.dataset.rotatorReady = '1';
      (root.dataset.words || '').split('|').forEach(function (woord, i) {
        woord = woord.trim();
        if (!woord || i === 0) return;
        var span = document.createElement('span');
        span.className = 'rk-hero__word';
        span.setAttribute('aria-hidden', 'true');
        span.textContent = woord;
        root.appendChild(span);
      });
    }

    var woorden = root.querySelectorAll('.rk-hero__word');
    if (woorden.length < 2) {
      if (woorden[0]) woorden[0].classList.add('is-active');
      return;
    }

    // De hoogte van de wisselregel vastzetten op het langste woord, zodat
    // de sub-tekst en de knoppen niet verspringen bij elke wissel.
    var hoogste = 0;
    woorden.forEach(function (w) {
      hoogste = Math.max(hoogste, w.getBoundingClientRect().height);
    });
    if (hoogste) root.style.minHeight = hoogste + 'px';

    // Pas nu mogen de regels op elkaar liggen; de hoogte staat vast.
    root.classList.add('is-ready');

    var i = 0;
    woorden[0].classList.add('is-active');
    if (rustig) return;

    setInterval(function () {
      woorden[i].classList.remove('is-active');
      i = (i + 1) % woorden.length;
      woorden[i].classList.add('is-active');
    }, parseInt(root.dataset.interval || '3200', 10));
  }

  /* ---------------------------------------------------------------- 2 */
  // In de statement-sectie licht telkens één zin op, met de bijbehorende
  // beelden. Dat liep eerst mee met de scrollpositie, maar dat las onrustig:
  // de zin die oplichtte hing af van hoe snel je scrolde. Nu wisselt hij op
  // tijd, en wie met de muis over een zin gaat zet hem zelf aan; het ronddraaien
  // pauzeert dan tot de muis weer weg is.
  function highlight(sectie) {
    var zinnen = sectie.querySelectorAll('.rk-highlight__phrase');
    var frames = sectie.querySelectorAll('.rk-highlight__frame');
    if (!zinnen.length) return;

    function zet(index) {
      zinnen.forEach(function (z, n) {
        z.classList.toggle('is-active', n === index);
      });
      frames.forEach(function (frame) {
        var beelden = frame.querySelectorAll('img');
        beelden.forEach(function (img, n) {
          img.classList.toggle('is-active', n === index % beelden.length);
        });
      });
    }

    zet(0);

    if (rustig) {
      zinnen.forEach(function (z) {
        z.classList.add('is-active');
      });
      return;
    }

    var i = 0;
    var klok = null;
    var inBeeld = true;
    var onderMuis = false;
    var tempo = parseInt(sectie.dataset.rkInterval, 10);
    if (!tempo || tempo < 1500) tempo = 4000;

    function stop() {
      if (klok) {
        window.clearInterval(klok);
        klok = null;
      }
    }

    function loop() {
      stop();
      if (!inBeeld || onderMuis) return;
      klok = window.setInterval(function () {
        i = (i + 1) % zinnen.length;
        zet(i);
      }, tempo);
    }

    zinnen.forEach(function (zin, n) {
      zin.addEventListener('pointerenter', function () {
        onderMuis = true;
        i = n;
        zet(n);
        stop();
      });
      zin.addEventListener('pointerleave', function () {
        onderMuis = false;
        loop();
      });
    });

    // Niet doorlopen zolang de sectie niet in beeld staat.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          inBeeld = entries[0].isIntersecting;
          loop();
        },
        { threshold: 0.15 }
      ).observe(sectie);
    }

    loop();
  }

  /* ---------------------------------------------------------------- 3 */
  function reveal() {
    var blokken = document.querySelectorAll('.rk-reveal');
    if (!blokken.length) return;

    if (rustig || !('IntersectionObserver' in window)) {
      blokken.forEach(function (b) {
        b.classList.add('is-visible');
      });
      return;
    }

    var waarnemer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-visible');
          waarnemer.unobserve(e.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    blokken.forEach(function (b) {
      waarnemer.observe(b);
    });
  }

  /* ---------------------------------------------------------------- 4 */
  // Het megamenu: bijhouden of er een paneel openstaat.
  //
  // Aurora zet de hoogte van het achtergrondvlak in een inline maat en hangt
  // pas na afloop van die beweging het kenmerk `with-opened-dropdown` op het
  // vlak. Bij het sluiten gaat dat kenmerk er meteen af. De regels die bepalen
  // welk paneel zichtbaar is hangen aan dat kenmerk, dus tijdens het openen en
  // tijdens het sluiten gold er even niets — en stonden alle panelen tegelijk
  // aan.
  //
  // Hier wordt de stand daarom aan de hoogte zelf ontleend: hoogte betekent
  // open. Dat is in beide richtingen op hetzelfde moment waar. De opmaak hangt
  // aan `rk-menu-open` op de balk.
  function megamenuStand() {
    var vlak = document.querySelector('[data-header-dropdown-backdrop]');
    var balk = vlak && vlak.closest('.header');
    if (!balk || !('MutationObserver' in window)) return;
    // in de themeditor wordt de sectie opnieuw opgebouwd; dan hangt er soms al
    // een waarnemer aan dit vlak
    if (vlak.dataset.rkGevolgd) return;
    vlak.dataset.rkGevolgd = '1';

    function lees() {
      var hoogte = parseFloat(vlak.style.getPropertyValue('--gsc-header-backdrop-height')) || 0;
      balk.classList.toggle('rk-menu-open', hoogte > 0);
    }

    new MutationObserver(lees).observe(vlak, {
      attributes: true,
      attributeFilter: ['style'],
    });
    lees();
  }

  /* ---------------------------------------------------------------- 5 */
  // Het mobiele menu hangt onder de balk. Aurora houdt daar zelf een maat voor
  // bij (`--gsc-header-bottom-side`), maar die staat er naast zodra de balk bij
  // het scrollen een stukje zakt. Daarom wordt de onderkant van de balk hier
  // zelf gemeten, op de momenten dat hij kan veranderen: bij het openen van het
  // menu en bij een maatverandering van het scherm.
  function headerOnderkant() {
    var balk = document.querySelector('.header__container');
    var paneel = document.querySelector('drawer-menu');
    if (!balk) return;

    function meet() {
      var onder = balk.getBoundingClientRect().bottom;
      document.documentElement.style.setProperty('--rk-header-onder', onder + 'px');
    }

    if (paneel && 'MutationObserver' in window) {
      new MutationObserver(meet).observe(paneel, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    window.addEventListener('resize', meet);
    meet();
  }

  /* ---------------------------------------------------------------- 6 */
  // Video's die uit zichzelf spelen: de themavideo op een productpagina en een
  // hero met een video als achtergrond. Ze starten pas als ze in beeld staan —
  // het bestand is zwaar en hoeft niet geladen te worden voor wie er nooit
  // langskomt — en buiten beeld gaan ze weer op pauze. Wie liever geen beweging
  // heeft, of op een verbinding zit die databesparing aan heeft staan, krijgt
  // de knoppen van de browser en start hem zelf.
  function themaVideo() {
    var videos = document.querySelectorAll(
      'video[data-rk-video], .rk-hero__video--auto video'
    );
    if (!videos.length) return;

    var verbinding = navigator.connection || {};
    var zuinig = verbinding.saveData === true;

    if (rustig || zuinig || !('IntersectionObserver' in window)) {
      videos.forEach(function (v) {
        v.controls = true;
        v.removeAttribute('autoplay');
      });
      return;
    }

    var waarnemer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          var v = e.target;
          if (e.isIntersecting) {
            v.preload = 'auto';
            var poging = v.play();
            // Weigert de browser alsnog, dan blijft de knop over.
            if (poging && poging.catch) {
              poging.catch(function () {
                v.controls = true;
              });
            }
          } else if (!v.paused) {
            v.pause();
          }
        });
      },
      { threshold: 0.25 }
    );

    videos.forEach(function (v) {
      // `muted` moet ook als eigenschap gezet zijn; alleen het attribuut is
      // voor sommige browsers niet genoeg om vanzelf te mogen starten.
      v.muted = true;
      waarnemer.observe(v);
    });
  }

  /* ---------------------------------------------------------------- 7 */
  // Een ingesloten YouTube- of Vimeo-video in de hero. De bron staat nog in een
  // data-attribuut, zodat we hem hier eerst kunnen bijstellen: wie liever geen
  // beweging heeft of op databesparing zit, krijgt de speler stil en met knoppen
  // in plaats van een video die uit zichzelf begint.
  function heroEmbed(scope) {
    var verbinding = navigator.connection || {};
    var zuinig = verbinding.saveData === true;

    (scope || document).querySelectorAll('iframe[data-rk-embed]').forEach(function (frame) {
      var bron = frame.getAttribute('data-rk-embed');
      if (!bron) return;

      // Staat de video op dit scherm uit (`Video ook op smalle schermen`), dan
      // laden we de speler helemaal niet; het achtergrondbeeld doet het werk.
      if (frame.offsetParent === null) return;

      if (rustig || zuinig) {
        bron = bron
          .replace('autoplay=1', 'autoplay=0')
          .replace('background=1', 'background=0')
          .replace('controls=0', 'controls=1');
      }

      frame.removeAttribute('data-rk-embed');
      frame.src = bron;
    });
  }

  /* ---------------------------------------------------------------- start */
  function start(scope) {
    (scope || document).querySelectorAll('.rk-hero__rotator').forEach(heroRotator);
    (scope || document).querySelectorAll('[data-rk-highlight]').forEach(highlight);
    reveal();
    megamenuStand();
    headerOnderkant();
    themaVideo();
    heroEmbed(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      start();
    });
  } else {
    start();
  }

  // In de themeditor wordt een sectie opnieuw opgebouwd; dan opnieuw starten.
  document.addEventListener('shopify:section:load', function (e) {
    start(e.target);
  });
})();
